import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ContentExperimentStatus, } from '@wit-support/admin-contracts';
import { Badge, Button, Card, Dialog, DialogContent, DialogDescription, DialogTitle, EmptyState, ErrorCard, LoadingTableCard, Pagination, SELECT_ALL_VALUE, SectionCard, SelectField, Table, clampPage, formatCount, formatNumberRu, pageCountOf, toast, } from '@wit-support/ui';
import { LineChart } from '@wit-support/ui/charts';
import { CONTENT_EXPERIMENT_STATUS_LABEL_RU, CONTENT_NOUN_FORMS, CONTENT_PROBE_OFF_LABEL_RU, CONTENT_RULE_PARAM_HINT_RU, CONTENT_RULE_PARAM_LABEL_RU, ContentExperimentDiff, ContentExperimentStatusBadge, ContentMultiple, Time, addDaysYmd, formatInZone, formatMultiple, startOfDayUtc, useAdminTimezone, } from '@wit-support/ui-domain';
import { Actions, CancelButton, SubmitButton, ZodField, ZodForm } from '@wit-support/ui-forms';
import { FlaskConical, Plus } from 'lucide-react';
import { createExperiment, listExperiments, updateExperiment } from '../api/experiments.js';
import { CONTENT_KEY, ContentScreen, describeContentError, submitting, useContentAccounts, } from '../shared.js';
import { useUrlSearch } from '../url-search.js';
import { readExperimentsSearch } from './clips-url.js';
const YMD = /^\d{4}-\d{2}-\d{2}$/;
// ---------------------------------------------------------------------------
// Формы
// ---------------------------------------------------------------------------
/**
 * Новый эксперимент. Плечо — имя и до трёх параметров правила (пусто —
 * как в правилах сети). Даты — календарные дни в таймзоне админа; начало —
 * с 00:00 дня, конец — до конца выбранного дня.
 */
export const ExperimentCreateFormSchema = z
    .object({
    name: z
        .string()
        .trim()
        .min(1, 'Укажите название')
        .max(200, 'Не длиннее 200 символов')
        .meta({ label: 'Название', placeholder: 'Например: пауза клипа 5 дней' }),
    accounts: z
        .array(z.string())
        .min(1, 'Выберите хотя бы один аккаунт')
        .max(100)
        .meta({ label: 'Аккаунты', widget: 'multiselect', placeholder: 'Выберите аккаунты…' }),
    startDate: z.string().regex(YMD, 'Укажите дату начала').meta({ label: 'Начало' }),
    endDate: z
        .string()
        .max(10)
        .meta({
        label: 'Конец (необязательно)',
        description: 'Без даты эксперимент идёт, пока его не завершат.',
    })
        .optional(),
    variants: z
        .array(z.object({
        name: z
            .string()
            .trim()
            .min(1, 'Укажите имя плеча')
            .max(100, 'Не длиннее 100 символов')
            .meta({ label: 'Имя плеча' }),
        strongMultiple: z
            .number({ message: 'Число' })
            .min(1.01, 'Не меньше ×1,01')
            .max(20, 'Не больше ×20')
            .meta({ label: CONTENT_RULE_PARAM_LABEL_RU.strongMultiple, widget: 'decimal' })
            .optional(),
        hookRestDays: z
            .number({ message: 'Целое число' })
            .int('Целое число дней')
            .min(0, 'Не меньше 0')
            .max(60, 'Не больше 60')
            .meta({
            label: CONTENT_RULE_PARAM_LABEL_RU.hookRestDays,
            hint: CONTENT_RULE_PARAM_HINT_RU.hookRestDays,
        })
            .optional(),
        probeEvery: z
            .number({ message: 'Целое число' })
            .int('Целое число')
            .min(0, 'Не меньше 0')
            .max(100, 'Не больше 100')
            .meta({
            label: CONTENT_RULE_PARAM_LABEL_RU.probeEvery,
            hint: CONTENT_RULE_PARAM_HINT_RU.probeEvery,
        })
            .optional(),
    }))
        .min(2, 'Нужно хотя бы два плеча: контроль и вариант')
        .max(10, 'Не больше 10 плеч')
        .meta({
        label: 'Плечи',
        description: 'Первое плечо — контроль: с ним сравниваются остальные. Пустой параметр — как в правилах сети.',
        addLabel: 'Добавить плечо',
        removeLabel: 'Убрать',
        itemLabel: 'Плечо',
    }),
})
    .superRefine((v, ctx) => {
    // Повтор имени — ошибка у самого поля-повтора: у плеча, а не у всего списка.
    const seen = new Set();
    v.variants.forEach((x, i) => {
        const name = x.name.trim();
        if (name !== '' && seen.has(name)) {
            ctx.addIssue({
                code: 'custom',
                path: ['variants', i, 'name'],
                message: 'Имена плеч должны различаться',
            });
        }
        seen.add(name);
    });
    const end = v.endDate?.trim() ?? '';
    if (end !== '' && !YMD.test(end)) {
        ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Дата в формате ГГГГ-ММ-ДД' });
    }
    else if (end !== '' && YMD.test(v.startDate) && end < v.startDate) {
        ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Конец не раньше начала' });
    }
});
/** Значения формы → тело `POST /experiments` (даты — в таймзоне админа). */
export function toExperimentCreateRequest(v, tz) {
    const startAt = startOfDayUtc(v.startDate, tz) ?? new Date(`${v.startDate}T00:00:00Z`).toISOString();
    const end = v.endDate?.trim() ?? '';
    const endAt = end !== '' ? startOfDayUtc(addDaysYmd(end, 1), tz) : undefined;
    return {
        name: v.name.trim(),
        accounts: v.accounts,
        startAt,
        ...(endAt !== undefined ? { endAt } : {}),
        variants: v.variants.map((x) => ({
            name: x.name.trim(),
            params: {
                ...(x.strongMultiple !== undefined ? { strongMultiple: x.strongMultiple } : {}),
                ...(x.hookRestDays !== undefined ? { hookRestDays: x.hookRestDays } : {}),
                ...(x.probeEvery !== undefined ? { probeEvery: x.probeEvery } : {}),
            },
        })),
    };
}
export const ExperimentEditFormSchema = z
    .object({
    status: ContentExperimentStatus.meta({
        label: 'Состояние',
        options: ContentExperimentStatus.options.map((s) => ({
            value: s,
            label: CONTENT_EXPERIMENT_STATUS_LABEL_RU[s],
        })),
    }),
    endDate: z
        .string()
        .max(10)
        .meta({
        label: 'Конец (включительно)',
        description: 'Пусто — без даты конца.',
    })
        .optional(),
})
    .superRefine((v, ctx) => {
    const end = v.endDate?.trim() ?? '';
    if (end !== '' && !YMD.test(end)) {
        ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Дата в формате ГГГГ-ММ-ДД' });
    }
});
/** Календарный день конца эксперимента в таймзоне админа (конец хранится как начало следующего дня). */
function endDayYmd(endAt, tz) {
    if (!endAt)
        return '';
    // Конец хранится как 00:00 следующего дня — показываем последний день.
    return formatInZone(new Date(new Date(endAt).getTime() - 1), 'yyyy-MM-dd', tz);
}
/**
 * Значения формы правки → тело `PATCH` только с тем, что изменилось;
 * `null` — ничего не изменилось. Пустая дата при заданном конце — снять конец.
 */
export function toExperimentUpdateRequest(e, v, tz) {
    const body = {};
    if (v.status !== e.status)
        body.status = v.status;
    const end = v.endDate?.trim() ?? '';
    const before = endDayYmd(e.endAt, tz);
    if (end !== before) {
        body.endAt = end === '' ? null : (startOfDayUtc(addDaysYmd(end, 1), tz) ?? null);
    }
    return body.status === undefined && body.endAt === undefined ? null : body;
}
// ---------------------------------------------------------------------------
// Экран
// ---------------------------------------------------------------------------
export function ContentExperimentsPage({ canManage }) {
    const [creating, setCreating] = useState(false);
    return (_jsxs(ContentScreen, { title: "\u042D\u043A\u0441\u043F\u0435\u0440\u0438\u043C\u0435\u043D\u0442\u044B", description: "\u041F\u0440\u043E\u0432\u0435\u0440\u043A\u0430 \u043F\u0440\u0430\u0432\u0438\u043B \u0432\u044B\u0431\u043E\u0440\u0430 \u043A\u043B\u0438\u043F\u0430 \u043D\u0430 \u0447\u0430\u0441\u0442\u0438 \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u043E\u0432: \u043F\u043B\u0435\u0447\u0438 \u0441\u0440\u0430\u0432\u043D\u0438\u0432\u0430\u044E\u0442\u0441\u044F \u0441 \u043A\u043E\u043D\u0442\u0440\u043E\u043B\u0435\u043C \u043F\u043E \u043A\u0440\u0430\u0442\u043D\u043E\u0441\u0442\u0438 \u0440\u043E\u043B\u0438\u043A\u043E\u0432 Instagram, \u0438\u0442\u043E\u0433\u0438 \u043F\u0435\u0440\u0435\u0441\u0447\u0438\u0442\u044B\u0432\u0430\u044E\u0442\u0441\u044F \u043D\u043E\u0447\u044C\u044E.", actions: canManage ? (_jsxs(Button, { onClick: () => setCreating(true), children: [_jsx(Plus, { className: "h-4 w-4", "aria-hidden": true }), " \u041D\u043E\u0432\u044B\u0439 \u044D\u043A\u0441\u043F\u0435\u0440\u0438\u043C\u0435\u043D\u0442"] })) : null, children: [_jsx(ExperimentsBody, { canManage: canManage }), creating ? _jsx(CreateDialog, { onClose: () => setCreating(false) }) : null] }));
}
function ExperimentsBody({ canManage }) {
    const [raw, setSearch] = useUrlSearch();
    const search = readExperimentsSearch(raw);
    const q = useQuery({
        queryKey: ['content', 'experiments', search],
        queryFn: () => listExperiments(search),
        placeholderData: keepPreviousData,
    });
    const [editing, setEditing] = useState(null);
    const total = q.data?.total ?? null;
    useEffect(() => {
        if (total === null || total === 0 || q.isPlaceholderData)
            return;
        const last = clampPage(search.page, pageCountOf(total, search.pageSize));
        if (last !== search.page)
            setSearch({ page: last === 1 ? undefined : last }, { replace: true });
    }, [total, search.page, search.pageSize, q.isPlaceholderData, setSearch]);
    const items = q.data?.items ?? [];
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex flex-wrap gap-3", children: _jsx(SelectField, { id: "content-experiments-status", label: "\u0421\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0435", value: search.status ?? SELECT_ALL_VALUE, allLabel: "\u0412\u0441\u0435", onChange: (v) => setSearch({ status: v === SELECT_ALL_VALUE ? undefined : v, page: undefined }), options: ContentExperimentStatus.options.map((s) => ({
                        value: s,
                        label: CONTENT_EXPERIMENT_STATUS_LABEL_RU[s],
                    })) }) }), q.isError ? (_jsx(ErrorCard, { children: describeContentError(q.error) })) : q.isLoading ? (_jsx(LoadingTableCard, { headerCells: ['Эксперимент', 'Плечи', 'Итог'], rows: 3 })) : items.length === 0 ? (_jsx(Card, { children: _jsx(EmptyState, { icon: FlaskConical, title: search.status ? 'Таких экспериментов нет' : 'Экспериментов пока нет', description: canManage
                        ? 'Создайте эксперимент, чтобы сравнить параметры выбора клипа на части аккаунтов.'
                        : 'Эксперименты запускают супервайзер или владелец.' }) })) : (_jsx("ul", { className: "flex flex-col gap-4", children: items.map((e) => (_jsx("li", { children: _jsx(ExperimentCard, { e: e, ...(canManage ? { onEdit: () => setEditing(e) } : {}) }) }, e.id))) })), q.data && (q.data.total ?? 0) > 0 ? (_jsx(Pagination, { id: "content-experiments-pagination", page: search.page, pageSize: search.pageSize, total: q.data.total, isFetching: q.isFetching, onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }), onPageSizeChange: (s) => setSearch({ pageSize: s, page: undefined }) })) : null, editing ? _jsx(EditDialog, { e: editing, onClose: () => setEditing(null) }) : null] }));
}
function paramsText(p) {
    const out = [];
    if (p.strongMultiple !== null)
        out.push(`сильный от ${formatMultiple(p.strongMultiple)}`);
    if (p.hookRestDays !== null)
        out.push(`пауза первым ${formatCount(p.hookRestDays, CONTENT_NOUN_FORMS.days)}`);
    if (p.probeEvery !== null)
        out.push(p.probeEvery === 0
            ? `проба ${CONTENT_PROBE_OFF_LABEL_RU}`
            : `проба раз в ${formatCount(p.probeEvery, CONTENT_NOUN_FORMS.videos)}`);
    for (const [k, v] of Object.entries(p.other ?? {}))
        out.push(`${k}: ${String(v)}`);
    return out;
}
function share(x) {
    return x === null ? '—' : `${formatNumberRu(x * 100, { maximumFractionDigits: 0 })} %`;
}
function ExperimentCard({ e, onEdit }) {
    const control = e.variants[0]?.name ?? null;
    return (_jsxs(SectionCard, { title: _jsxs("span", { className: "inline-flex flex-wrap items-center gap-2", children: [e.name, " ", _jsx(ContentExperimentStatusBadge, { status: e.status })] }), hint: _jsxs("p", { className: "text-sm text-[var(--color-text-muted)]", children: [_jsx(Time, { iso: e.startAt, preset: "date" }), " \u2014", ' ', e.endAt ? (_jsx(Time, { iso: new Date(new Date(e.endAt).getTime() - 1).toISOString(), preset: "date" })) : ('без даты конца'), ' · ', formatCount(e.accounts.length, CONTENT_NOUN_FORMS.accounts), ": ", e.accounts.join(', ')] }), actions: onEdit ? (_jsx(Button, { size: "sm", variant: "secondary", onClick: onEdit, children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C" })) : null, children: [_jsxs(Table, { density: "compact", children: [_jsx(Table.Header, { children: _jsxs(Table.Row, { children: [_jsx(Table.HeaderCell, { children: "\u041F\u043B\u0435\u0447\u043E" }), _jsx(Table.HeaderCell, { children: "\u041F\u0430\u0440\u0430\u043C\u0435\u0442\u0440\u044B" })] }) }), _jsx(Table.Body, { children: e.variants.map((v) => {
                            const t = paramsText(v.params);
                            return (_jsxs(Table.Row, { children: [_jsx(Table.Cell, { className: "whitespace-nowrap", children: _jsxs("span", { className: "inline-flex items-center gap-1.5", children: [v.name, v.name === control ? _jsx(Badge, { variant: "outline", children: "\u043A\u043E\u043D\u0442\u0440\u043E\u043B\u044C" }) : null] }) }), _jsx(Table.Cell, { className: "text-[var(--color-text-muted)]", children: t.length > 0 ? t.join(' · ') : 'как в правилах сети' })] }, v.name));
                        }) })] }), e.results === null ? (_jsx("p", { className: "text-sm text-[var(--color-text-muted)]", children: "\u0418\u0442\u043E\u0433\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442 \u2014 \u043D\u043E\u0447\u043D\u043E\u0439 \u043F\u0435\u0440\u0435\u0441\u0447\u0451\u0442 \u0435\u0449\u0451 \u043D\u0435 \u0441\u0447\u0438\u0442\u0430\u043B \u044D\u0442\u043E\u0442 \u044D\u043A\u0441\u043F\u0435\u0440\u0438\u043C\u0435\u043D\u0442." })) : (_jsxs(_Fragment, { children: [_jsxs("p", { className: "text-sm font-medium", children: ["\u0418\u0442\u043E\u0433 \u043D\u0430 ", _jsx(Time, { iso: e.results.asOf, preset: "date" })] }), _jsx(StatsTable, { variants: e.results.overall.variants, diffs: e.results.overall.diffs }), _jsx(WeeklyStats, { e: e })] }))] }));
}
function StatsTable({ variants, diffs, week, }) {
    return (_jsxs(Table, { density: "compact", children: [_jsx(Table.Header, { children: _jsxs(Table.Row, { children: [week !== undefined ? _jsx(Table.HeaderCell, { children: "\u041D\u0435\u0434\u0435\u043B\u044F" }) : null, _jsx(Table.HeaderCell, { children: "\u041F\u043B\u0435\u0447\u043E" }), _jsx(Table.HeaderCell, { className: "text-right", children: "\u0420\u043E\u043B\u0438\u043A\u043E\u0432" }), _jsx(Table.HeaderCell, { children: "\u041C\u0435\u0434\u0438\u0430\u043D\u0430" }), _jsx(Table.HeaderCell, { className: "text-right", children: "\u0417\u0430\u043B\u0451\u0442\u044B" }), _jsx(Table.HeaderCell, { children: "\u0425\u0443\u0434\u0448\u0438\u0435 10 %" }), _jsx(Table.HeaderCell, { children: "\u041F\u0440\u043E\u0442\u0438\u0432 \u043A\u043E\u043D\u0442\u0440\u043E\u043B\u044F" })] }) }), _jsx(Table.Body, { children: variants.map((v) => {
                    const d = diffs.find((x) => x.variant === v.variant);
                    return (_jsxs(Table.Row, { children: [week !== undefined ? (_jsx(Table.Cell, { className: "whitespace-nowrap", children: _jsx(Time, { iso: `${week}T12:00:00.000Z`, preset: "date" }) })) : null, _jsx(Table.Cell, { children: v.variant }), _jsx(Table.Cell, { className: "text-right tabular-nums", children: formatNumberRu(v.n, { maximumFractionDigits: 0 }) }), _jsx(Table.Cell, { children: _jsx(ContentMultiple, { value: v.medianMultiple }) }), _jsx(Table.Cell, { className: "text-right tabular-nums", children: share(v.hitShare) }), _jsx(Table.Cell, { children: _jsx(ContentMultiple, { value: v.p10Multiple }) }), _jsx(Table.Cell, { children: d ? (_jsx(ContentExperimentDiff, { diff: d })) : (_jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u043A\u043E\u043D\u0442\u0440\u043E\u043B\u044C" })) })] }, v.variant));
                }) })] }));
}
/** Контроль — серый, плечи — категориальная палитра графиков. */
const VARIANT_TONES = [
    'neutral',
    'chart-1',
    'chart-2',
    'chart-3',
    'chart-4',
    'chart-5',
    'chart-6',
];
/** Медиана кратности плеч по неделям: строка на неделю, ряд на плечо. */
export function weeklyMedianRows(e) {
    return (e.results?.weeks ?? []).map((w) => {
        const row = { week: w.week };
        for (const v of w.variants)
            row[v.variant] = v.medianMultiple;
        return row;
    });
}
function WeeklyStats({ e }) {
    const weeks = e.results?.weeks ?? [];
    if (weeks.length === 0)
        return null;
    const rows = weeklyMedianRows(e);
    return (_jsxs("div", { className: "flex flex-col gap-3", children: [_jsx("p", { className: "text-sm font-medium", children: "\u041F\u043E \u043D\u0435\u0434\u0435\u043B\u044F\u043C" }), _jsx(LineChart, { ariaLabel: `Медиана кратности по неделям: ${e.name}`, data: rows, x: "week", height: 180, valueFormat: (v) => formatMultiple(v), series: e.variants.map((v, i) => ({
                    key: v.name,
                    label: v.name,
                    tone: VARIANT_TONES[Math.min(i, VARIANT_TONES.length - 1)],
                })) }), weeks.map((w) => (_jsx(StatsTable, { variants: w.variants, diffs: w.diffs, week: w.week }, w.week)))] }));
}
// ---------------------------------------------------------------------------
// Диалоги (только content.manage — кнопки без права не рисуются, admin-api
// всё равно проверяет политику)
// ---------------------------------------------------------------------------
function CreateDialog({ onClose }) {
    const qc = useQueryClient();
    const tz = useAdminTimezone();
    const { accounts } = useContentAccounts();
    const today = formatInZone(new Date(), 'yyyy-MM-dd', tz);
    const mut = useMutation({
        mutationFn: (body) => createExperiment(body),
        onSuccess: (r) => {
            toast.success(`Эксперимент «${r.name}» создан`);
            onClose();
            void qc.invalidateQueries({ queryKey: CONTENT_KEY });
        },
        onError: (err) => toast.error(describeContentError(err, 'write')),
    });
    return (_jsx(Dialog, { open: true, onOpenChange: (v) => (!v ? onClose() : null), children: _jsxs(DialogContent, { className: "max-h-[90dvh] overflow-y-auto", children: [_jsx(DialogTitle, { children: "\u041D\u043E\u0432\u044B\u0439 \u044D\u043A\u0441\u043F\u0435\u0440\u0438\u043C\u0435\u043D\u0442" }), _jsx(DialogDescription, { children: "\u0410\u043A\u043A\u0430\u0443\u043D\u0442\u044B \u043F\u043E \u043D\u0435\u0434\u0435\u043B\u044F\u043C \u0440\u0430\u0441\u043F\u0440\u0435\u0434\u0435\u043B\u044F\u044E\u0442\u0441\u044F \u043C\u0435\u0436\u0434\u0443 \u043F\u043B\u0435\u0447\u0430\u043C\u0438; \u043F\u043B\u0435\u0447\u043E \u043C\u0435\u043D\u044F\u0435\u0442 \u043F\u0430\u0440\u0430\u043C\u0435\u0442\u0440\u044B \u0432\u044B\u0431\u043E\u0440\u0430 \u043F\u0435\u0440\u0432\u043E\u0433\u043E \u043A\u043B\u0438\u043F\u0430. \u0414\u0435\u0439\u0441\u0442\u0432\u0443\u0435\u0442 \u0441\u043E \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0435\u0433\u043E \u0440\u043E\u043B\u0438\u043A\u0430 \u043F\u043E\u0441\u043B\u0435 \u043D\u0430\u0447\u0430\u043B\u0430." }), _jsxs(ZodForm, { schema: ExperimentCreateFormSchema, defaultValues: {
                        name: '',
                        accounts: [],
                        startDate: today,
                        endDate: '',
                        variants: [{ name: 'Контроль' }, { name: 'Вариант Б' }],
                    }, fieldMetaOverrides: {
                        accounts: {
                            label: 'Аккаунты',
                            widget: 'multiselect',
                            placeholder: 'Выберите аккаунты…',
                            options: accounts.map((a) => ({ value: a, label: a })),
                        },
                    }, onSubmit: (values) => submitting(mut.mutateAsync(toExperimentCreateRequest(values, tz))), children: [_jsx(ZodField, { name: "name" }), _jsx(ZodField, { name: "accounts" }), _jsxs("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [_jsx(ZodField, { name: "startDate", type: "date" }), _jsx(ZodField, { name: "endDate", type: "date" })] }), _jsx(ZodField, { name: "variants" }), _jsxs(Actions, { children: [_jsx(CancelButton, { onClick: onClose }), _jsx(SubmitButton, { pendingLabel: "\u0421\u043E\u0437\u0434\u0430\u0451\u043C\u2026", children: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C" })] })] })] }) }));
}
function EditDialog({ e, onClose }) {
    const qc = useQueryClient();
    const tz = useAdminTimezone();
    const mut = useMutation({
        mutationFn: (body) => updateExperiment(e.id, body),
        onSuccess: () => {
            toast.success('Эксперимент сохранён');
            onClose();
            void qc.invalidateQueries({ queryKey: CONTENT_KEY });
        },
        onError: (err) => toast.error(describeContentError(err, 'write')),
    });
    return (_jsx(Dialog, { open: true, onOpenChange: (v) => (!v ? onClose() : null), children: _jsxs(DialogContent, { children: [_jsxs(DialogTitle, { children: ["\u042D\u043A\u0441\u043F\u0435\u0440\u0438\u043C\u0435\u043D\u0442 \u00AB", e.name, "\u00BB"] }), _jsx(DialogDescription, { children: "\u0417\u0430\u0432\u0435\u0440\u0448\u0451\u043D\u043D\u044B\u0439 \u044D\u043A\u0441\u043F\u0435\u0440\u0438\u043C\u0435\u043D\u0442 \u043F\u0435\u0440\u0435\u0441\u0442\u0430\u0451\u0442 \u043C\u0435\u043D\u044F\u0442\u044C \u0432\u044B\u0431\u043E\u0440 \u043A\u043B\u0438\u043F\u0430; \u0438\u0442\u043E\u0433\u0438 \u043E\u0441\u0442\u0430\u044E\u0442\u0441\u044F." }), _jsxs(ZodForm, { schema: ExperimentEditFormSchema, defaultValues: { status: e.status, endDate: endDayYmd(e.endAt, tz) }, onSubmit: (values) => {
                        const body = toExperimentUpdateRequest(e, values, tz);
                        if (body === null) {
                            onClose();
                            return;
                        }
                        return submitting(mut.mutateAsync(body));
                    }, children: [_jsx(ZodField, { name: "status" }), _jsx(ZodField, { name: "endDate", type: "date" }), _jsxs(Actions, { children: [_jsx(CancelButton, { onClick: onClose }), _jsx(SubmitButton, { pendingLabel: "\u0421\u043E\u0445\u0440\u0430\u043D\u044F\u0435\u043C\u2026", children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" })] })] })] }) }));
}
//# sourceMappingURL=experiments.js.map